import React, { useState, useEffect } from 'react';
import BaseModal from '../base/BaseModal';
import { XCircle, AlertCircle, Loader2 } from 'lucide-react';
import { REPORT_API } from '../../apis/report';
import { showToast } from '../../utils/toastNotification';

export default function ReportRejectModal({
  isOpen,
  onClose,
  report,
  onSuccess
}) {
  const [rejectReason, setRejectReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setRejectReason('');
      setIsSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen || !report) return null;

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      showToast.error('Vui lòng nhập lý do từ chối phản ánh');
      return;
    }

    setIsSubmitting(true);
    try {
      await REPORT_API.updateStatusReport(report.id, {
        trangThai: 'Đóng',
        ghiChu: `Từ chối tiếp nhận: ${rejectReason.trim()}`,
      });

      showToast.success('Đã từ chối đơn phản ánh thành công');
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      showToast.error(err?.message || 'Có lỗi xảy ra khi từ chối phản ánh');
    } finally {
      setIsSubmitting(false);
    }
  };

  const categoryName = report.linh_vuc_phan_anh?.ten || report.linh_vuc?.ten || 'Chưa phân loại';
  const neighborhood = report.khu_pho || 'Chưa rõ khu phố';
  const location = report.vi_tri || 'Không có thông tin vị trí';

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Từ chối đơn phản ánh"
      subtitle={`Mã phản ánh: ${report.ma_phan_anh || report.id}`}
      size="md"
      className="max-w-lg"
    >
      <div className="space-y-4 py-1">
        {/* Tóm tắt phản ánh Card */}
        <div className="bg-gray-50/80 border border-gray-200/80 rounded-lg p-3.5 space-y-2">
          <h4 className="text-sm font-bold text-gray-900 leading-snug m-0 line-clamp-2">
            {report.tieu_de || 'Phản ánh kiến nghị'}
          </h4>

          <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 pt-1 border-t border-gray-200/60">
            <div>
              <span className="text-gray-500">Lĩnh vực: </span>
              <span className="font-semibold text-gray-800">{categoryName}</span>
            </div>
            <div>
              <span className="text-gray-500">Khu phố: </span>
              <span className="font-semibold text-gray-800">{neighborhood}</span>
            </div>
            <div className="col-span-2 truncate">
              <span className="text-gray-500">Vị trí: </span>
              <span className="font-medium text-gray-800">{location}</span>
            </div>
          </div>
        </div>

        {/* Khung cảnh báo và nhập lý do */}
        <div className="bg-red-50/50 border border-red-200 rounded-lg p-3.5 space-y-3">
          <div className="flex items-center gap-2 text-red-700 font-bold text-xs md:text-sm">
            <AlertCircle className="w-4 h-4 text-red-600 shrink-0" />
            <span>Xác nhận từ chối tiếp nhận phản ánh</span>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Lý do từ chối tiếp nhận <span className="text-red-500">*</span>
            </label>
            <textarea
              id="reject-reason"
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              required
              aria-required="true"
              placeholder="Nhập lý do từ chối phản ánh để thông báo tới người dân (ví dụ: Không thuộc thẩm quyền xử lý, thông tin không chính xác...)..."
              className="w-full px-3 py-2 text-xs md:text-sm bg-white border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-400 placeholder:text-gray-400"
            />
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 rounded-md text-xs md:text-sm font-semibold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-all cursor-pointer"
          >
            Hủy bỏ
          </button>

          <button
            type="button"
            onClick={handleReject}
            disabled={isSubmitting || !rejectReason.trim()}
            className="px-4 py-2 rounded-md text-xs md:text-sm font-bold text-white bg-red-600 hover:bg-red-700 shadow-xs transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
          >
            {isSubmitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <XCircle className="w-4 h-4" />
            )}
            <span>Xác nhận từ chối</span>
          </button>
        </div>
      </div>
    </BaseModal>
  );
}
